import {Injectable, inject} from '@angular/core';
import {BaseApiService} from '../base-api.service';
import {FirebaseApi} from '../../firebase-api.service';
import {UserInfo} from '../../../model/user-info';

/** Another account, as far as the caller may see it. */
export interface PublicProfile {
  uid: string;
  fullName: string;
  photoUrl: string;
  /** Only where the caller already knows it (same organization, same project). */
  email?: string;
}

/** Where a lookup is made from -- the server only answers for people there. */
export interface DirectoryContext {
  projectId?: string;
  folderId?: string;
}

/**
 * Other people's names and photos.
 *
 * Other users' documents are private to their owner (and to an organization
 * owner for their members): they hold billing addresses, tax ids and Stripe
 * ids. The server answers with public fields only, and only for people the
 * caller has a reason to see.
 *
 * Lookups are cached for the session, including "not visible", so views that
 * ask on every change detection (the last editor's avatar on a shape) do not
 * turn into a request storm.
 */
@Injectable({
  providedIn: 'root',
})
export class DirectoryApiService extends BaseApiService {

  private readonly firebase = inject(FirebaseApi);
  /**
   * Keyed by the signed-in uid too, so a sign-out never leaks into the next
   * session. Null is "not visible"; undefined is "the request failed".
   */
  private cache = new Map<string, Promise<PublicProfile | null | undefined>>();
  private organization = new Map<string, Promise<PublicProfile[]>>();

  constructor() {
    super();
  }

  /**
   * Profiles for these accounts. Anyone the caller may not see is left out
   * of the result. With `failOnError`, a request that failed rejects rather
   * than leaving its people out: a list that must be complete (who a project
   * is shared with) can then say it could not be loaded.
   */
  lookup(uids: string[], context: DirectoryContext = {},
         options: {failOnError?: boolean} = {}): Promise<PublicProfile[]> {
    const wanted = [...new Set((uids ?? []).filter(uid => !!uid))];
    const missing = wanted.filter(uid => !this.cache.has(this.key(uid, context)));
    for (let i = 0; i < missing.length; i += 50) {
      const batch = missing.slice(i, i + 50);
      const keys = batch.map(uid => this.key(uid, context));
      const request = this.post<PublicProfile[]>('/directory/users', {uids: batch, ...context})
        .then(result => result ?? [])
        .catch(() => {
          // A failed request is not an answer: ask again next time.
          keys.forEach(key => this.cache.delete(key));
          return null as PublicProfile[] | null;
        });
      batch.forEach((uid, index) => this.cache.set(keys[index],
        request.then(profiles => profiles ? (profiles.find(p => p.uid === uid) ?? null) : undefined)));
    }
    return Promise.all(wanted.map(uid => this.cache.get(this.key(uid, context))))
      .then(profiles => {
        if (options.failOnError && profiles.some(p => p === undefined)) {
          throw new Error('Directory lookup failed');
        }
        return profiles.filter(p => !!p);
      });
  }

  /** One profile, or null when the caller may not see it. */
  lookupOne(uid: string, context: DirectoryContext = {}): Promise<PublicProfile | null> {
    return this.lookup([uid], context).then(profiles => profiles[0] ?? null);
  }

  /** Everyone in the caller's organization except the caller. */
  getOrganization(): Promise<PublicProfile[]> {
    const me = this.firebase.getUserId() ?? '';
    if (!this.organization.has(me)) {
      this.organization.set(me, this.get<PublicProfile[]>('/directory/organization')
        .then(result => result ?? [])
        .catch(err => {
          this.organization.delete(me);
          throw err;
        }));
    }
    return this.organization.get(me);
  }

  /** A profile in the shape views that used to read user documents expect. */
  static asUserInfo(profile: PublicProfile): UserInfo {
    const info = new UserInfo();
    info.uid = profile.uid;
    info.fullName = profile.fullName ?? '';
    info.photoUrl = profile.photoUrl ?? '';
    info.email = profile.email ?? '';
    return info;
  }

  private key(uid: string, context: DirectoryContext): string {
    return `${this.firebase.getUserId() ?? ''}|${context.projectId ?? ''}|${context.folderId ?? ''}|${uid}`;
  }
}
